import { Clock, Search } from 'react-feather';
import { Spin, Typography } from 'antd';
import { fontStyles, iconStyles } from 'styles/theme';
import styled, { CSSObject } from '@emotion/styled';

import { ReactComponent as BuildingSVG } from 'images/building.svg';
import ContactButton from 'components/common/ContactButton';
import FlexContainer from 'components/common/FlexContainer';
import useTranslation from 'hooks/use-translation';

type Props = {
  isRecent: boolean;
  nameLabel: string;
  countryLabel: string;
};

export function InstitutionSearchLabel(props: Props): JSX.Element {
  const { countryLabel, isRecent, nameLabel } = props;
  const translation = useTranslation();

  let defaultIcon = <DefaultIcon className='institution-search-default-icon' />;

  if (translation?.common.icons.institutionSearchDropdownItem) {
    defaultIcon = (
      <img
        src={`./config/icons/${translation.common.icons.institutionSearchDropdownItem.src}`}
        alt={translation.common.icons.institutionSearchDropdownItem.alt}
        width={18}
        height={18}
        className='institution-search-default-icon'
      />
    );
  }

  return (
    <Container>
      {isRecent ? <RecentIcon className='institution-search-recent-icon' size={18} /> : defaultIcon}
      <Typography.Paragraph ellipsis={{ rows: 2, expandable: false }}>
        {nameLabel}
        {isRecent && <CountryLabel>{countryLabel}</CountryLabel>}
      </Typography.Paragraph>
    </Container>
  );
}

const Container = styled.div({
  display: 'flex',
  flexDirection: 'row',
  gap: 16,
  '& div.ant-typography': {
    margin: 0,
  },
});

const iconCSS: CSSObject = {
  position: 'relative',
  top: 3,
  color: iconStyles.colors.primary,
};

const RecentIcon = styled(Clock)(iconCSS);

const DefaultIcon = styled(BuildingSVG)(iconCSS);

const CountryLabel = styled.span({
  marginLeft: 8,
  color: iconStyles.colors.primary,
  fontSize: fontStyles.sizes.sm,
});

export const SearchIcon = styled(Search)({
  color: iconStyles.colors.primary,
  '&:hover': {
    cursor: 'pointer',
  },
});

export function NoResultsFound(): JSX.Element {
  const translation = useTranslation();

  if (!translation) {
    return <Spin />;
  }

  return (
    <NoResultsContainer>
      <NoResultsHeading>{translation.home.institutionSearch.notFoundContent.heading}</NoResultsHeading>
      <NoResultsDescription>{translation.home.institutionSearch.notFoundContent.description}</NoResultsDescription>
      <ContactButton />
    </NoResultsContainer>
  );
}

const NoResultsContainer = styled(FlexContainer)({
  padding: '32px 50px',
  color: fontStyles.colors.primary,
  gap: 16,
});

const NoResultsHeading = styled.span({
  fontSize: fontStyles.sizes.xl,
});

const NoResultsDescription = styled.span({
  fontSize: fontStyles.sizes.sm,
  lineHeight: '20px',
});
